import React from 'react';
import { useParams } from 'react-router-dom';
import { Label } from '@/components/atoms/texts/label';
import PrintErrorMessage from '@/components/organisms/feedback/PrintErrorMessage';
import PrintInformationMessage from '@/components/organisms/feedback/PrintInformationMessage';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Button } from '@/components/molecules/buttons/button';
import StatementOperationsChart from '@/components/organisms/charts/StatementOperationsChart';
import DetailsAboutOverflowOperation from './DetailsAboutOverflowOperation';
import Filter from '@/components/organisms/Filter';
import EditAdminModal from '@/components/macro-organisms/EditAdminModal';
import DialogConfirmDelete from '@/components/organisms/dialog/DialogConfirmDelete';
import OperationItem from '@/components/macro-organisms/tabs/consultants-vision-admin/overflow/OperationItem';
import StatementOperationForm from '@/components/macro-organisms/tabs/consultants-vision-admin/overflow/StatementOperationForm';
import EditOperationFormContent from '@/components/macro-organisms/tabs/consultants-vision-admin/overflow/EditOperationFormContent';
import InterestsHoldDialogs from '@/components/macro-organisms/tabs/consultants-vision-admin/overflow/InterestsHoldDialogs';
import InterestsHoldIcon from '@/components/molecules/InterestsHoldIcon';
import InterestsHoldActiveWarning from '@/components/organisms/feedback/InterestsHoldActiveWarning';
import InterestsHoldUpcomingInformation from '@/components/organisms/feedback/InterestsHoldUpcomingInformation';
import { Snowflake } from 'lucide-react';
import { useOveflowAccountTab } from '@/hooks/global-hooks/useOveflowAccountTab';
import { useStatementInterestsHolds } from '@/hooks/global-hooks/useStatementInterestsHolds';

export default function OverflowAccountTab({ isAdmin = false }) {
    const { id } = useParams();
    const holdsState = useStatementInterestsHolds({ consultantUuid: id, isAdmin });

    const {
        t,
        year,
        loading,
        error,
        addLoading,
        updateLoading,
        updateAlwaysLoading,
        deleteLoading,
        allYears,
        yearStatement,
        yearStatementLoading,
        yearStatementError,
        filteredGroups,
        displayedBalance,
        canManageStatement,
        detailledEvent,
        setDetailledEvent,
        setSelectedYear,
        submitRef,
        isEditModalOpen,
        editingOperation,
        editMode,
        submissionError,
        submissionSuccess,
        setSubmissionSuccess,
        setSubmissionError,
        alwaysEditableForm,
        setAlwaysEditableForm,
        editOperationForm,
        setEditOperationForm,
        isCreateModalOpen,
        setIsCreateModalOpen,
        deleteConfirmOpen,
        operationToDelete,
        createOperationForm,
        setCreateOperationForm,
        isAlwaysEditableSubmitDisabled,
        isManualEditSubmitDisabled,
        isCreateOperationSubmitDisabled,
        currencies,
        loadingCurrencies,
        errorCurrencies,
        getSelectedEntityHookData,
        getOperationTypeModalType,
        getModalTitle,
        handleOpenEditOperation,
        handleRequestDeleteOperation,
        handleConfirmDeleteOperation,
        handleCloseDeleteDialog,
        handleEditModalClose,
        handleSubmitAlwaysEditable,
        handleSubmitManualEditOperation,
        handleSubmitTypedForm,
        handleCreateManualOperation,
    } = useOveflowAccountTab({ isAdmin });

    return (
        <div className={`rounded-lg  px-thirdDegree w-full flex gap-secondDegree justify-center ${isAdmin ? '' : 'h-full'}`}>
            <div className={`flex flex-col w-full gap-secondDegree  min-w-[200px]  overflow-auto px-thirdDegree ${isAdmin ? 'h-[62vh]' : 'h-full'}`}>
                <div className={`flex flex-col gap-secondDegree items-center ${isAdmin ? '' : 'pt-thirdDegree'}`}>
                    {!isAdmin && <Label size="h2" message="overflow.overflowAccount" fontWeight="bold" />}
                    {(loading || yearStatementLoading || addLoading || updateLoading || updateAlwaysLoading || deleteLoading) && <span className="loader" />}
                    {error && <PrintErrorMessage error={error} />}
                    {yearStatementError && <PrintErrorMessage error={yearStatementError} />}
                    <InterestsHoldActiveWarning hold={holdsState.activeHold} className="w-full" />
                    <InterestsHoldUpcomingInformation hold={holdsState.upcomingHold} className="w-full" />

                    <div className=" w-full justify-end gap-firstDegree grid grid-cols-3 items-center ">
                        <div className="w-full flex col-start-2 justify-center">
                            {(allYears?.length > 0) && (
                                <Filter
                                    items={allYears}
                                    informSelectedItem={setSelectedYear}
                                />
                            )}
                        </div>
                        {canManageStatement && (
                            <div className="flex items-center justify-end gap-firstDegree">
                                {holdsState.canManageHolds && (
                                    <>
                                        <Button
                                            className="lg:hidden w-fit"
                                            style="dressedGhost"
                                            leftIcon={<Snowflake className="h-5 w-5" />}
                                            showLeftIcon
                                            showText={false}
                                            onClick={holdsState.openCreateHold}
                                        />
                                        <Button
                                            className="hidden lg:inline-flex w-fit"
                                            style="dressedGhost"
                                            leftIcon={<Snowflake className="h-5 w-5" />}
                                            showLeftIcon
                                            text="consultants.statement.interestsHolds.freeze"
                                            onClick={holdsState.openCreateHold}
                                        />
                                    </>
                                )}
                                <Button
                                    className="lg:hidden w-fit"
                                    style="dressedGhost"
                                    leftIcon={<AddRoundedIcon />}
                                    showLeftIcon
                                    showText={false}
                                    onClick={() => setIsCreateModalOpen(true)}
                                />
                                <Button
                                    className="hidden lg:inline-flex w-fit"
                                    style="dressedGhost"
                                    leftIcon={<AddRoundedIcon />}
                                    showLeftIcon
                                    text="consultants.editModal.newOperation"
                                    onClick={() => setIsCreateModalOpen(true)}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col justify-between items-center p-thirdDegree">
                    {displayedBalance !== null ? (
                        <Label fontWeight="bold" size="h1">
                            {displayedBalance.balance.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                        </Label>
                    ) : (
                        <Label fontWeight="bold" color="disabled" size="h3">---</Label>
                    )}
                    <div className=" flex items-center gap-betweenText">
                        <Label message="overflow.investmentBalanceAt" color="primaryStrong" size="small" />
                        <Label color="primaryStrong" size="small">
                            {displayedBalance?.date?.toLocaleDateString('fr-FR') || '—'}
                        </Label>
                    </div>
                </div>

                {filteredGroups.length !== 0 && <StatementOperationsChart
                    year={year}
                    lightStyle
                    dataOwner={isAdmin}
                    yearStatement={yearStatement}
                    interestsHolds={holdsState.holds}
                    onHoldClick={holdsState.canManageHolds ? holdsState.openEditHold : undefined}
                />}

                {filteredGroups.length === 0 ? (
                    (loading || yearStatementLoading) ? <span className="loader" /> : <PrintInformationMessage information="error.noStatementOperationsForYear" />)
                    : (
                        <div className="flex flex-col gap-secondDegree">
                            {filteredGroups.map((group) => (
                                <div key={group.key} className="flex flex-col gap-secondDegree">
                                    <div className="flex items-center justify-between p-firstDegree">
                                        <div className="flex items-center gap-firstDegree">
                                            <Label fontWeight="semibold">{group.label}</Label>
                                            <InterestsHoldIcon
                                                holds={[holdsState.getHoldForDate(group.day || group.date)].filter(Boolean)}
                                                onClick={holdsState.openEditHold}
                                            />
                                        </div>
                                        <div className="flex items-end gap-firstDegree">
                                            {group.evenements.filter((ev) => Number(ev.amountEur ?? ev.amount ?? 0) !== 0 && !ev.interestDate).length > 1 && (
                                                <Label showToolTip fontWeight="semibold" color="primaryStrong" tooltip={'overflow.totalDayOperations'}>
                                                    {group.total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                                </Label>
                                            )}
                                            <Label showToolTip fontWeight="" color="primaryStrong" tooltip={'overflow.soldeAfterDayOperations'}>
                                                {(() => {
                                                    const ops = group.evenements.filter((ev) => ev.afterBalance !== undefined && ev.afterBalance !== null);
                                                    const ints = group.evenements.filter((ev) => ev.interestsBalanceEur !== undefined && ev.interestsBalanceEur !== null);
                                                    if (ops.length > 0) {
                                                        return `${Number(ops[ops.length - 1].afterBalance).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
                                                    }
                                                    if (ints.length > 0) {
                                                        return `${Number(ints[ints.length - 1].interestsBalanceEur).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
                                                    }
                                                    return '—';
                                                })()}
                                            </Label>
                                        </div>
                                    </div>
                                    <div className=" flex flex-col  bg-white shadow-sm rounded-sm min-h-[52px]">
                                        {group.evenements
                                            .filter((ev) => Number(ev.amountEur ?? ev.amount ?? 0) !== 0 && !ev.interestDate)
                                            .map((ev, idx) => (
                                                <OperationItem
                                                    item={{
                                                        kind: ev.interestDate ? 'interest' : 'operation',
                                                        payload: ev,
                                                    }}
                                                    setDetailledEvent={setDetailledEvent}
                                                    canManage={canManageStatement}
                                                    onEdit={handleOpenEditOperation}
                                                    onDelete={handleRequestDeleteOperation}
                                                    key={(ev.uuid ?? idx) + '-' + idx}
                                                />
                                            ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
            </div>

            {detailledEvent && <div className=" flex items-center p-firstDegree justify-center  rounded-sm w-full max-w-[600px]">
                <div className="relative flex flex-col gap-firstDegree backdrop-blur-md hover:bg-[#FFFFFF20]   p-firstDegree items-center justify-center  w-full  rounded-sm ">
                    <DetailsAboutOverflowOperation
                        type={detailledEvent?.operationType}
                        uuid={detailledEvent?.linkedEntityUuid}
                        isAdmin={isAdmin}
                    />
                    <Button className="absolute left-firstDegree top-firstDegree rounded-sm" style="ghost" sizePadding="small" showLeftIcon leftIcon={<CloseRoundedIcon />} showText={false} onClick={() => setDetailledEvent(false)} />
                </div>
            </div>}

            {isEditModalOpen && editingOperation && (
                <EditAdminModal
                    data={editingOperation}
                    onClose={handleEditModalClose}
                    type={getOperationTypeModalType(editingOperation.operationType)}
                    title={getModalTitle(editingOperation.operationType)}
                    mediumSize={true}
                    form={
                        <EditOperationFormContent
                            editMode={editMode}
                            editingOperation={editingOperation}
                            alwaysEditableForm={alwaysEditableForm}
                            setAlwaysEditableForm={setAlwaysEditableForm}
                            submissionError={submissionError}
                            editOperationForm={editOperationForm}
                            setEditOperationForm={setEditOperationForm}
                            currencies={currencies}
                            loadingCurrencies={loadingCurrencies}
                            errorCurrencies={errorCurrencies}
                            t={t}
                            getSelectedEntityHookData={getSelectedEntityHookData}
                            handleSubmitTypedForm={handleSubmitTypedForm}
                            submitRef={submitRef}
                            submissionSuccess={submissionSuccess}
                            setSubmissionSuccess={setSubmissionSuccess}
                            setSubmissionError={setSubmissionError}
                        />
                    }
                    onSubmitForm={editMode === 'alwaysEditable'
                        ? handleSubmitAlwaysEditable
                        : editMode === 'manualForm'
                            ? handleSubmitManualEditOperation
                            : () => submitRef.current && submitRef.current.click()}
                    disabledSubmit={editMode === 'manualForm' ? isManualEditSubmitDisabled : isAlwaysEditableSubmitDisabled}
                />
            )}

            {isCreateModalOpen && (
                <EditAdminModal
                    data={createOperationForm}
                    onClose={() => setIsCreateModalOpen(false)}
                    type="timesheet"
                    title="consultants.editModal.newOperation"
                    mediumSize={true}
                    form={
                        <StatementOperationForm
                            formData={createOperationForm}
                            setFormData={setCreateOperationForm}
                            currencies={currencies}
                            loadingCurrencies={loadingCurrencies}
                            errorCurrencies={errorCurrencies}
                            t={t}
                        />
                    }
                    onSubmitForm={handleCreateManualOperation}
                    disabledSubmit={isCreateOperationSubmitDisabled}
                />
            )}

            <InterestsHoldDialogs holdsState={holdsState} />

            <DialogConfirmDelete
                open={deleteConfirmOpen}
                onClose={handleCloseDeleteDialog}
                onConfirm={handleConfirmDeleteOperation}
                title={operationToDelete?.operationType ? `Opération ${operationToDelete.operationType}` : 'Opération'}
            />
        </div>
    );
}
